import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import i18n from "../../../i18n";
import { serverErrorMessage } from "../../../utils/serverError";
import { Button, ModalShell, Toggle } from "./atoms";
import { useLifeAreas } from "./config";
import { SettingsSkeleton } from "./LoadingSkeleton";
import { getSettings, putSettings } from "./ringsApi";
import { fs, T, withAlpha } from "./tokens";
import { DimensionKey, RingSettings, RingSnapshot } from "./types";
import { getAppLanguage, toLocale } from "../../../utils/appLanguage";

type Props = {
  snapshot: RingSnapshot;
  onClose: () => void;
  onSaved: (snapshot: RingSnapshot) => void;
};

// Weekday / hour names come from the user's language, not from a hardcoded list.
const dayName = (d: number) => new Date(2023, 0, 1 + d).toLocaleDateString(toLocale(getAppLanguage()), { weekday: "long" }); // 2023-01-01 is a Sunday
const hourName = (h: number) => new Date(2000, 0, 1, h).toLocaleTimeString(toLocale(getAppLanguage()), { hour: "numeric" });

const selectStyle: React.CSSProperties = {
  ...fs("sm"),
  background: T.color.bg.card,
  color: T.color.text.primary,
  border: `1px solid ${T.color.bg.overlay}`,
  borderRadius: T.radius.lg,
  padding: "8px 12px",
};

const SettingsModal: React.FC<Props> = ({ snapshot, onClose, onSaved }) => {
  const L = useLifeAreas();
  const { t } = useTranslation();
  const [settings, setSettings] = useState<RingSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettings()
      .then(setSettings)
      .catch(() => {
        toast.error(i18n.t("lifeAreas.settingsModal.loadFailed"));
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleActive = (key: DimensionKey) =>
    setSettings((s) => {
      if (!s) return s;
      const off = s.inactiveDimensions.includes(key)
        ? s.inactiveDimensions.filter((k) => k !== key)
        : [...s.inactiveDimensions, key];
      return { ...s, inactiveDimensions: off };
    });

  const save = async () => {
    if (!settings) return;
    if (settings.inactiveDimensions.length >= snapshot.dimensions.length) {
      toast.error(t("lifeAreas.settingsModal.keepOne"));
      return;
    }
    setSaving(true);
    try {
      const { snapshot: next } = await putSettings({
        inactiveDimensions: settings.inactiveDimensions,
        pinnedDimension: settings.pinnedDimension,
        reminder: settings.reminder,
        // the reminder time is read in the user's own timezone
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || settings.timezone,
      });
      onSaved(next);
      onClose();
    } catch (err: any) {
      toast.error(serverErrorMessage(err, "lifeAreas.settingsModal.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell ariaLabel={t("lifeAreas.settingsModal.title")} title={t("lifeAreas.settingsModal.title")} onClose={onClose} focusKey={settings === null}>
      {!settings ? (
        <SettingsSkeleton />
      ) : (
        <>
          <p style={{ ...fs("xs"), color: T.color.text.secondary, margin: "0 0 10px" }}>
            {t("lifeAreas.settingsModal.intro")}
          </p>
          <ul style={{ margin: 0, padding: 0, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {L.areas.map((area) => {
              const k = area.key;
              const off = settings.inactiveDimensions.includes(k);
              return (
                <li
                  key={k}
                  style={{
                    listStyle: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "8px 12px",
                    background: T.color.bg.card,
                    border: `1px solid ${off ? T.color.bg.overlay : withAlpha(area.color, 0.5)}`,
                    borderRadius: T.radius.lg,
                    opacity: off ? 0.6 : 1,
                  }}
                >
                  <span style={{ ...fs("sm", 500) }}>
                    <span aria-hidden="true">{area.emoji}</span> {area.label}
                  </span>
                  <Toggle checked={!off} onChange={() => toggleActive(k)} label={t("lifeAreas.settingsModal.active", { area: area.label })} />
                </li>
              );
            })}
          </ul>

          <label style={{ ...fs("sm"), display: "block", marginTop: 16, color: T.color.text.secondary }}>
            {t("lifeAreas.settingsModal.pinned")}
            <select
              value={settings.pinnedDimension ?? ""}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  pinnedDimension: (e.target.value || null) as DimensionKey | null,
                })
              }
              style={{
                ...fs("sm"),
                display: "block",
                width: "100%",
                marginTop: 6,
                background: T.color.bg.card,
                color: T.color.text.primary,
                border: `1px solid ${T.color.bg.overlay}`,
                borderRadius: T.radius.lg,
                padding: "8px 12px",
              }}
            >
              <option value="">{t("lifeAreas.settingsModal.noPinned")}</option>
              {L.areas.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>

          <div style={{ marginTop: 16, padding: 12, background: T.color.bg.card, borderRadius: T.radius.lg }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", ...fs("sm", 500) }}>
              {t("lifeAreas.settingsModal.remind")}
              <Toggle
                checked={settings.reminder.enabled}
                onChange={(v) => setSettings({ ...settings, reminder: { ...settings.reminder, enabled: v } })}
                label={t("lifeAreas.settingsModal.remind")}
              />
            </div>
            {settings.reminder.enabled && (
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <select
                  aria-label={t("lifeAreas.settingsModal.day")}
                  value={settings.reminder.dayOfWeek}
                  onChange={(e) =>
                    setSettings({ ...settings, reminder: { ...settings.reminder, dayOfWeek: Number(e.target.value) } })
                  }
                  style={selectStyle}
                >
                  {Array.from({ length: 7 }, (_, d) => (
                    <option key={d} value={d}>
                      {dayName(d)}
                    </option>
                  ))}
                </select>
                <select
                  aria-label={t("lifeAreas.settingsModal.hour")}
                  value={settings.reminder.hour}
                  onChange={(e) =>
                    setSettings({ ...settings, reminder: { ...settings.reminder, hour: Number(e.target.value) } })
                  }
                  style={selectStyle}
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {hourName(h)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <p style={{ ...fs("xs"), color: T.color.text.secondary, margin: "8px 0 0" }}>
              {t("lifeAreas.settingsModal.reminderNote")}
            </p>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
            <Button variant="ghost" onClick={onClose}>
              {t("lifeAreas.steps.cancel")}
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? t("lifeAreas.checkin.saving") : t("lifeAreas.settingsModal.save")}
            </Button>
          </div>
        </>
      )}
    </ModalShell>
  );
};

export default SettingsModal;
